import * as THREE from "three";
import { useLayoutEffect, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, extend, useFrame } from "@react-three/fiber";
import {
  Image,
  ScrollControls,
  useScroll,
  Billboard,
  Text,
} from "@react-three/drei";
import { suspend } from "suspend-react";
import { generate } from "random-words";
import { easing, geometry } from "maath";
import gallery from "./content/gallery_data.json";

extend(geometry);

const webfont = import("./fonts/adobe-garamond-pro_normal_500.woff");

const content = [
  { title: "Hey, sugar", images: 6 },
  { title: "The story", images: 4 },
  { title: "The samples", images: 4 },
  { title: "The women", images: gallery.length },
];

export const App = () => (
  <Canvas dpr={[1, 1.5]}>
    <color attach="background" args={["#000000"]} />
    <ScrollControls pages={4} infinite>
      <Scene position={[0, 1.5, 0]} />
    </ScrollControls>
  </Canvas>
);

function Scene({ children, ...props }) {
  const ref = useRef();
  const scroll = useScroll();
  const [hovered, hover] = useState(null);

  useFrame((state, delta) => {
    ref.current.rotation.y = -scroll.offset * (Math.PI * 2); // Rotate contents
    state.events.update(); // Raycasts every frame rather than on pointer-move
    easing.damp3(
      state.camera.position,
      [-state.pointer.x * 2, state.pointer.y * 2 + 4.5, 9],
      0.3,
      1
    );
    state.camera.lookAt(0, 0, 0);
  });
  useEffect(() => {
    document.body.style.cursor = hovered ? "pointer" : "auto";
  }, [hovered]);

  const gapFactor = 0.1; // Percentage of total available angle reserved for gaps

  // Compute total number of images
  const totalImages = content.reduce(
    (sum, category) => sum + category.images,
    0
  );

  // Compute total available angle minus space reserved for gaps
  const totalGapAngle = 2 * Math.PI * gapFactor;
  const availableAngle = 2 * Math.PI - totalGapAngle;

  return (
    <group ref={ref} {...props}>
      {
        content.reduce(
          (acc, data, i) => {
            const prevAngle = acc.prevAngle;
            const sectionAngle = (data.images / totalImages) * availableAngle; // Proportional allocation
            const from =
              prevAngle + (i > 0 ? totalGapAngle / content.length : 0); // Add a gap

            return {
              prevAngle: from + sectionAngle, // Move to next starting position
              elements: [
                ...acc.elements,
                <Cards
                  key={data.title}
                  category={data.title}
                  from={from}
                  sectionAngle={sectionAngle}
                  radius={5.25}
                  onPointerOver={hover}
                  onPointerOut={hover}
                  data={data}
                />,
              ],
            };
          },
          { prevAngle: 0, elements: [] }
        ).elements
      }
      <ActiveCard hovered={hovered} category="Hey, sugar" />
    </group>
  );
}

function Cards({
  category,
  data,
  from = 0,
  sectionAngle,
  radius = 5.25,
  onPointerOver,
  onPointerOut,
  ...props
}) {
  const [hovered, hover] = useState(null);
  const amount = data.images;

  return (
    <group {...props}>
      <Billboard
        position={[
          Math.sin(from + sectionAngle / 2) * radius * 1.35, // Center title
          0.5,
          Math.cos(from + sectionAngle / 2) * radius * 1.35,
        ]}
      >
        <Text
          font={suspend(webfont).default}
          fontSize={0.25}
          anchorX="center"
          color="white"
        >
          {category}
        </Text>
      </Billboard>
      {Array.from({ length: amount }, (_, i) => {
        const angle = from + (i / amount) * sectionAngle; // Spread cards proportionally
        return (
          <Card
            key={angle}
            onPointerOver={e => (
              e.stopPropagation(), hover(i), onPointerOver(i)
            )}
            onPointerOut={() => (hover(null), onPointerOut(null))}
            position={[Math.sin(angle) * radius, 0, Math.cos(angle) * radius]}
            rotation={[0, Math.PI / 2 + angle, 0]}
            active={hovered !== null}
            hovered={hovered === i}
            url={`/images/${category
              .toLowerCase()
              .replace(" ", "-")
              .replace(",", "")}/img${(i % 10) + 1}.jpg`}
          />
        );
      })}
    </group>
  );
}

function Card({ url, active, hovered, ...props }) {
  const ref = useRef();
  useFrame((state, delta) => {
    const f = hovered ? 1.4 : active ? 1.25 : 1;
    easing.damp3(ref.current.position, [0, hovered ? 0.25 : 0, 0], 0.1, delta);
    easing.damp3(ref.current.scale, [1.618 * f, 1 * f, 1], 0.15, delta);
  });
  return (
    <group {...props}>
      <Image
        ref={ref}
        transparent
        radius={0}
        url={url}
        scale={[1.618, 1, 1]}
        side={THREE.DoubleSide}
      />
    </group>
  );
}

function ActiveCard({ hovered, category, ...props }) {
  const ref = useRef();
  const name = useMemo(() => generate({ exactly: 2 }).join(" "), [hovered]);
  useLayoutEffect(() => void (ref.current.material.zoom = 1.2), [hovered]);
  useFrame((state, delta) => {
    easing.damp(ref.current.material, "zoom", 1, 0.5, delta);
    easing.damp(ref.current.material, "opacity", hovered !== null, 0.3, delta);
  });
  return (
    <Billboard {...props}>
      <Text
        font={suspend(webfont).default}
        fontSize={0.4}
        position={[0, 4, -1]}
        anchorX="left"
        color="white"
      >
        {hovered !== null && `${name}\n${hovered ? "hover" : "not hover"}`}
      </Text>
      <Image
        ref={ref}
        transparent
        radius={0}
        position={[0, 0, 1]}
        scale={[5, 5, 0.2, 1]}
        url={`/images/${category
          .toLowerCase()
          .replace(" ", "-")
          .replace(",", "")}/img${Math.floor(hovered % 10) + 1}.jpg`}
      />
    </Billboard>
  );
}
